import { useContext, useState, useMemo } from 'react';
import constate from 'constate';
import { intersection } from 'lodash';
import { useHistory } from 'react-router-dom';

import { getStorageKey } from '../core/utils/storage';
import { UserContext } from '../contexts/index.jsx';
import { AddressHelper } from '../helpers';
import {
  useCheckoutOrder,
  usePayDeposits,
  usePayOrder,
  useRemoveOrderItem,
} from './orderManagementApi';
import {
  getItemAvailableDeposits,
  getItemPaidDeposits,
} from './helpers/checkout';
import {
  preparePaymentData,
} from './helpers/payment';

function generateBankTransferRefNo(userId) {
  return 'RC' + userId + "O" +
    Math.floor(Math.random() * Math.floor(10000)).toString(16).toUpperCase()
}

const [CheckoutProvider, useCheckout] = constate(({ orderId }) => {
  const history = useHistory();
  const { user } = useContext(UserContext);

  const { order, items: cartItems, isLoading } = useCheckoutOrder(orderId);

  const { removeOrderItem } = useRemoveOrderItem(!orderId);
  const { payOrder } = usePayOrder();
  const { payDeposits } = usePayDeposits();

  const defaultAddress = useMemo(() => AddressHelper.cloneUserAddress(user), [user]);
  const [shippingAddress, setShippingAddress] = useState(order?.shipping_address || defaultAddress)
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [transferRefNo, setTransferRefNo] = useState(generateBankTransferRefNo(user.id));
  const [submitting, setSubmitting] = useState(false);

  const paymentMethodsOfSelectedDeposits = useMemo(() => {
    const paymentMethods = selectedDeposits.map((deposit) => deposit.paymentMethods);

    return intersection(...paymentMethods);
  }, [selectedDeposits]);

  const availablePaymentMethods = useMemo(() => {
    if (!cartItems.length) {
      return ['transfer'];
    }

    if (selectedDeposits.length) {
      if (!paymentMethodsOfSelectedDeposits.includes(selectedPaymentMethod)) {
        setSelectedPaymentMethod(paymentMethodsOfSelectedDeposits[0]);
      }

      return paymentMethodsOfSelectedDeposits;
    }

    const cardEnabledForAllListings = cartItems.every((item) => item.listing.allow_card_payments);

    const availableMethods = [
      'transfer',
      ...(cardEnabledForAllListings ? ['card'] : []),
    ];

    if (!availableMethods.includes(selectedPaymentMethod)) {
      setSelectedPaymentMethod(availableMethods[0]);
    }

    return availableMethods;
  }, [cartItems,selectedDeposits]);

  const items = useMemo(() => {
    return cartItems?.map((item) => {
      const paidAmount = item.deposits.reduce((acc, deposit) => acc + deposit.amount, 0);

      return {
        id: item.id,
        price: item.price,
        quantity: item.quantity,
        listing: item.listing,
        paidDeposits: getItemPaidDeposits(item.deposits),
        availableDeposits: getItemAvailableDeposits(
          item,
          selectedDeposits,
          paymentMethodsOfSelectedDeposits,
        ),
        paidAmount,
      };
    }) || [];
  }, [cartItems, selectedDeposits, paymentMethodsOfSelectedDeposits]);

  const paidAmount = useMemo(() => {
    return items.reduce((acc, item) => acc + item.paidAmount, order?.payment?.amount || 0);
  }, [order, items]);

  const payableAmount = useMemo(() => {
    if (selectedDeposits.length) {
      return selectedDeposits.reduce((acc, deposit) => acc + deposit.amount, 0);
    }

    const listingsTotalPrice = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

    return listingsTotalPrice - paidAmount;
  }, [cartItems, selectedDeposits, paidAmount]);

  const removeItem = async (itemId) => {
    try {
      await removeOrderItem(itemId);
    } catch (error) {
      console.error(error);
      // TODO
    }
  }

  const toggleDeposit = (deposit) => {
    setSelectedDeposits((selectedDeposits) => {
      if (selectedDeposits.find((selected) => selected.itemId === deposit.itemId && selected.name === deposit.name)) {
        return selectedDeposits.filter((selected) => selected.itemId !== deposit.itemId || selected.name !== deposit.name);
      }

      return [...selectedDeposits, deposit];
    });
  };

  const pay = async (paymentMethod, paymentSpecificData) => {
    try {
      setSubmitting(true);

      const isDepositPayment = selectedDeposits.length > 0;

      const result = await (isDepositPayment ? payDeposits : payOrder)(
        preparePaymentData(
          user.id,
          payableAmount,
          'gbp',
          cartItems,
          selectedDeposits,
          paymentMethod,
          paymentSpecificData,
          getStorageKey('referralCode'),
          paymentSpecificData?.billing_address,
          shippingAddress,
        )
      );

      const payment = isDepositPayment ? result : result.payment;
      const newOrder = isDepositPayment ? null : result;

      if (paymentMethod === 'offline') {
        onPaymentConfirmed(true, newOrder);
      } else {
        // Stripe payment is confirmed later
      }

      return { payment, order: newOrder };
    } catch (error) {
      console.error('Error while paying', error);
    } finally {
      setSubmitting(false);
    }
  }

  const onPaymentConfirmed = (success, order) => {
    if (!success) {
      console.error('Payment confirmation failed');
      return;
    }

    if (selectedDeposits.length > 0) {
      setSelectedDeposits([]);
      setTransferRefNo(generateBankTransferRefNo(user.id));
    } else {
      history.push(`/order/${order.reference_number}`)
    }
  };

  return {
    isLoading,
    count: items?.length || 0,
    total: items?.reduce((acc, item) => acc + item.price, 0) || 0,
    removeItem,
    submitting,
    pay,
    onPaymentConfirmed,
    items,
    paidAmount,
    payableAmount,
    shippingAddress,
    setShippingAddress,
    toggleDeposit,
    areDepositsSelected: selectedDeposits.length > 0,
    selectedDepositsAmount: selectedDeposits.reduce((acc, deposit) => acc + deposit.amount, 0),
    availablePaymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    transferRefNo,
    isOrderPaid: !payableAmount,
  };
});

export { CheckoutProvider, useCheckout };
